import React, { Fragment, useState, useRef, useEffect } from "react";
import axios from "axios";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS } from "../utils/init";
import {
  GetTransactionsByFilter,
  MarkAsTestTransaction,
  MarkAsRegularTransaction,
  ExportTransactionsCSV,
  update_payment_status,
  get_refund_list,
  update_refund_status,
  create_refund,
  get_network_fees,
  cancelSubscription,
  FetchUsdBtcLedgerAmount,
} from "../utils/service";
import { showConfirm, showToast, currentPageAccess } from "../utils/library";

/* ─── API Endpoints ─── */
const PAYBITO_ACCOUNTS = "https://accounts.paybito.com/api";

/* ─── Helpers ─── */
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const mapStatus = (s) => {
  if (!s) return "pending";
  const ll = s.toLowerCase();

  // Succeeded statuses
  if (
    [
      "success",
      "succeeded",
      "payment_confirmed",
      "closed_with_autometic_overpayment",
      "closed_with_exact_payment",
      "closed_with_manual_overpayment",
    ].includes(ll)
  )
    return "succeeded";

  // Cancelled statuses
  if (["subscription_canclled", "subscription_cancelled"].includes(ll))
    return "cancelled";

  // Failed statuses
  if (
    [
      "failed",
      "invoice_expired",
      "payment_failed",
      "closed_with_underpayment",
    ].includes(ll)
  )
    return "failed";

  // Refunded statuses
  if (["refunded", "refund_approved", "refund_requested"].includes(ll))
    return "refunded";

  // Processing statuses (these are in-progress, not pending)
  if (
    [
      "payment_created",
      "payment_processing",
      "payment_initiated",
      "waiting",
      "processing",
    ].includes(ll)
  )
    return "processing";

  // Pending statuses (waiting for user action)
  if (["pending"].includes(ll)) return "pending";

  return "pending";
};

const statusLabel = (s) => {
  if (!s) return "Processing";
  const labels = {
    payment_created: "Payment Created",
    payment_processing: "Payment Processing",
    payment_initiated: "Payment Initiated",
    payment_confirmed: "Payment Confirmed",
    closed_with_autometic_overpayment: "Closed with Overpayment",
    closed_with_exact_payment: "Closed",
    closed_with_manual_overpayment: "Closed with Overpayment",
    closed_with_underpayment: "Closed with Underpayment",
    invoice_expired: "Expired",
    payment_failed: "Failed",
    refunded: "Refunded",
    refund_approved: "Refund Approved",
    refund_requested: "Refund Requested",
    succeeded: "Succeeded",
    success: "Success",
    failed: "Failed",
    pending: "Pending",
    processing: "Processing",
    subscription_canclled: "Subscription Cancelled",
    subscription_cancelled: "Subscription Cancelled",
  };
  return labels[s.toLowerCase()] || cap(s.replace(/_/g, " "));
};

const fmtAmt = (amount, currency) => {
  if (amount == null || isNaN(amount)) return "—";

  // Handle small numbers (less than 0.000001) with up to 8 decimal places
  const num = Number(amount);

  const sym =
    currency === "USD"
      ? "$"
      : currency === "EUR"
      ? "€"
      : currency === "GBP"
      ? "£"
      : "";
  const suffix = ["USD", "EUR", "GBP"].includes(currency)
    ? ""
    : " " + (currency || "");

  // For very small numbers (like crypto amounts), show more decimals
  if (Math.abs(num) < 0.0001 && num !== 0) {
    return `${sym}${num.toFixed(8)}${suffix}`;
  }

  // For normal numbers, format with locale
  return `${sym}${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  })}${suffix}`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getTrialDaysLeft = (t) => {
  if (!t.isTrialAvailed || t.isTrialAvailed !== 1) return 0;
  const trialDays = parseInt(t.trialDays || 0);
  if (trialDays <= 0) return 0;
  const createdAt = t.paymentCreatedAt || t.txnDate;
  if (!createdAt) return 0;
  const trialEnd = new Date(createdAt);
  trialEnd.setDate(trialEnd.getDate() + trialDays);
  const now = new Date();
  const msLeft = trialEnd.getTime() - now.getTime();
  return msLeft > 0 ? Math.ceil(msLeft / (1000 * 60 * 60 * 24)) : 0;
};

const Transactions = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [dateFilter, setDateFilter] = useState("ALL");
  const [methodFilter, setMethodFilter] = useState("");
  const [networkFilter, setNetworkFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [markingAsTest, setMarkingAsTest] = useState(false);
  const [markingAsRegular, setMarkingAsRegular] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);

  // Refund modal state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundItem, setRefundItem] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("Customer Request");
  const [refundNote, setRefundNote] = useState("");
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);
  const [networkFee, setNetworkFee] = useState(0);
  const [networkFeeLoading, setNetworkFeeLoading] = useState(false);
  const networkFeeTimerRef = useRef(null);

  // Refund history and details
  const [refundHistory, setRefundHistory] = useState([]);
  const [refundDetailsOpen, setRefundDetailsOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [refundHistoryLoading, setRefundHistoryLoading] = useState(false);

  // More Filters state
  const [productName, setProductName] = useState("");
  const [catalogueName, setCatalogueName] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerIdentity, setCustomerIdentity] = useState("");
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [blockchainFilter, setBlockchainFilter] = useState("");
  const [assetFilter, setAssetFilter] = useState("");
  const [rewardFilter, setRewardFilter] = useState("");

  // API state
  const [showTestOnly, setShowTestOnly] = useState(false);
  const [txnList, setTxnList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabCounts, setTabCounts] = useState({
    totalCount: 0,
    succeededCount: 0,
    failedCount: 0,
    refundedCount: 0,
    cancelledCount: 0,
  });
  const [summary, setSummary] = useState({
    totalVolume: 0,
    successVolume: 0,
    failedVolume: 0,
    refundedVolume: 0,
    totalCount: 0,
    succeededCount: 0,
    failedCount: 0,
    refundedCount: 0,
    totalPages: 1,
    totalRecords: 0,
  });

  const tableRef = useRef(null);
  const searchTimerRef = useRef(null);

  const getStatusFilter = () => {
    if (showTestOnly) return "TEST";
    const map = {
      succeeded: "SUCCESS",
      failed: "FAILED",
      pending: "PROCESSING",
      refunded: "REFUNDED",
      processing: "PROCESSING",
      cancelled: "CANCELLED",
    };
    return map[activeFilter] || "";
  };

  const getDateRange = () => {
    if (dateFilter === "custom") return "CUSTOM";
    const map = {
      ALL: "ALL",
      today: "TODAY",
      "7d": "LAST_7_DAYS",
      "30d": "LAST_30_DAYS",
      month: "THIS_MONTH",
    };
    return map[dateFilter] || "ALL";
  };

  const resetMoreFilters = () => {
    setProductName("");
    setCatalogueName("");
    setSubscriptionId("");
    setCustomerId("");
    setCustomerIdentity("");
    setGatewayFilter("");
    setPaymentTypeFilter("");
    setBlockchainFilter("");
    setAssetFilter("");
    setRewardFilter("");
  };

  // Update the fetchTransactions function to include test mode filtering
  const fetchTransactions = (overrides = {}) => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (!merchantId) return;
    setLoading(true);

    // Map more filters to correct API fields
    const finalPaymentMethod = gatewayFilter || methodFilter;
    const finalNetwork = blockchainFilter || networkFilter;
    const finalCurrency = assetFilter || currencyFilter;

    const payload = {
      merchantId: parseInt(merchantId),
      status: getStatusFilter(),
      paymentMethod: finalPaymentMethod,
      network: finalNetwork,
      search: searchQ,
      productName: productName,
      catalogueName: catalogueName,
      subscriptionId: subscriptionId,
      customerId: customerId,
      customerIdentity: customerIdentity,
      paymentType: paymentTypeFilter,
      currency: finalCurrency,
      reward: rewardFilter,
      dateRange: getDateRange(),
      fromDate: dateFilter === "custom" ? customDateFrom : "",
      toDate: dateFilter === "custom" ? customDateTo : "",
      page: currentPage,
      pageSize,
      ...overrides,
    };

    GetTransactionsByFilter(payload)
      .then((res) => {
        const d = res.data;
        setTxnList(d.transactions || []);

        setSummary({
          totalVolume: d.totalVolume || 0,
          successVolume: d.successVolume || 0,
          failedVolume: d.failedVolume || 0,
          refundedVolume: d.refundedVolume || 0,
          totalCount: d.totalCount || 0,
          succeededCount: d.succeededCount || 0,
          failedCount: d.failedCount || 0,
          refundedCount: d.refundedCount || 0,
          totalPages: d.totalPages || 1,
          totalRecords: d.totalRecords || 0,
        });

        // Only update tab counts when on "all" filter (unfiltered) and not in test mode
        if (activeFilter === "all" && !showTestOnly) {
          setTabCounts({
            totalCount: d.totalCount || 0,
            succeededCount: d.succeededCount || 0,
            failedCount: d.failedCount || 0,
            refundedCount: d.refundedCount || 0,
            cancelledCount: d.cancelledCount || 0,
          });
        }
      })
      .catch(() => setTxnList([]))
      .finally(() => setLoading(false));
  };

  const handleTestUserFilter = () => {
    const next = !showTestOnly;
    setShowTestOnly(next);
    setCurrentPage(1);
    if (!next) clearSelection();
  };

  // Auto-select all rows when viewing test users
  useEffect(() => {
    if (showTestOnly && !loading && txnList.length > 0) {
      setSelectedIds(new Set(txnList.map((_, idx) => idx)));
    }
  }, [showTestOnly, loading, txnList]);

  // Fetch on filter/page changes (except search which is debounced)
  useEffect(() => {
    setCurrentPage(1);
    fetchTransactions({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeFilter,
    methodFilter,
    networkFilter,
    currencyFilter,
    dateFilter,
    customDateFrom,
    customDateTo,
    productName,
    catalogueName,
    subscriptionId,
    customerId,
    customerIdentity,
    gatewayFilter,
    paymentTypeFilter,
    blockchainFilter,
    assetFilter,
    rewardFilter,
    showTestOnly,
    pageSize,
  ]);

  // Handle page changes separately
  useEffect(() => {
    if (currentPage === 1) return;
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Debounced search
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchTransactions({ search: searchQ, page: 1 });
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ]);

  // Fetch refund history on component mount
  useEffect(() => {
    fetchRefundHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterDefs = [
    { key: "all", label: "All", count: tabCounts.totalCount },
    { key: "succeeded", label: "Success", count: tabCounts.succeededCount },
    { key: "failed", label: "Failed", count: tabCounts.failedCount },
    { key: "processing", label: "Processing", count: 0 }, // This will be calculated from API
    { key: "refunded", label: "Refunded", count: tabCounts.refundedCount },
    { key: "cancelled", label: "Cancelled", count: tabCounts.cancelledCount },
  ];

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleSelectAll = (checked) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      txnList.forEach((_, idx) => {
        checked ? n.add(idx) : n.delete(idx);
      });
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const openDrawer = (t) => {
    setDrawerItem(t);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerItem(null);
  };

  const hasFailedSelected = [...selectedIds].some((idx) => {
    const t = txnList[idx];
    return t && mapStatus(t.status) === "failed";
  });

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
    clearSelection();
    if (showTestOnly) setShowTestOnly(false); // Reset test filter when changing tabs
  };

  const applyMoreFilters = () => {
    setMoreFiltersOpen(false);
    setCurrentPage(1);
    fetchTransactions({ page: 1 });
  };

  const clearMoreFilters = () => {
    resetMoreFilters();
    setMoreFiltersOpen(false);
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (currentPageAccess() === "READ") {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    if (txnList.length === 0) {
      showToast("error", "No transactions to export.");
      return;
    }

    setExporting(true);

    try {
      const headers = [
        "Transaction ID",
        "Customer ID",
        "Customer Email",
        "Customer Identity",
        "Product Name",
        "Payment Type",
        "Currency",
        "Base Amount",
        "Fee Amount",
        "Total Amount",
        "Paid Amount",
        "Status",
        "Payment Method",
        "Network",
        "Session ID",
        "Created Date",
        "Processed Date",
        "Test Mode",
      ];

      const rows = txnList.map((t) => {
        const displayProductName =
          t.productDetails && t.productDetails.length > 0
            ? t.productDetails[0].productName
            : t.productName || "—";

        return [
          t.transactionId || "—",
          t.customerId || "—",
          t.customerEmail || "—",
          t.customerIdentity || "—",
          displayProductName,
          t.paymentType || "ONE-TIME",
          t.currency || "—",
          t.baseAmount || 0,
          t.feeAmount || 0,
          (t.baseAmount || 0) + (t.feeAmount || 0) - (t.discountAmount || 0),
          t.paidAmount || 0,
          t.status || "—",
          t.paymentMethod || "—",
          t.paymentSubDetails || "—",
          t.sessionId || "—",
          t.paymentCreatedAt
            ? new Date(t.paymentCreatedAt).toLocaleString()
            : "—",
          t.paymentProcessAt
            ? new Date(t.paymentProcessAt).toLocaleString()
            : "—",
          t.testMode === true ? "Yes" : "No",
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      link.setAttribute("href", url);
      link.setAttribute("download", `transactions_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      showToast("error", "Failed to export transactions. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleMarkAsTest = async () => {
    if (selectedIds.size === 0) {
      showToast(
        "error",
        "Please select at least one transaction to mark as test."
      );
      return;
    }

    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (!merchantId) return;

    setMarkingAsTest(true);
    try {
      const transactionIds = Array.from(selectedIds)
        .map((idx) => txnList[idx]?.transactionId)
        .filter(Boolean)
        .join(",");
      const payload = {
        merchantId: parseInt(merchantId),
        transactionId: transactionIds,
        status: "MARKED",
      };

      const response = await MarkAsTestTransaction(payload);

      if (response.data.status === true) {
        showToast(
          "success",
          `Successfully marked ${selectedIds.size} transaction(s) as test.`
        );
        clearSelection();
        fetchTransactions();
      } else {
        showToast(
          "error",
          response.data.message || "Failed to mark transactions as test."
        );
      }
    } catch (error) {
      console.error("Mark as test failed:", error);
      showToast(
        "error",
        "Failed to mark transactions as test. Please try again."
      );
    } finally {
      setMarkingAsTest(false);
    }
  };

  const handleMarkAsRegular = async () => {
    if (selectedIds.size === 0) {
      showToast("error", "Please select at least one transaction.");
      return;
    }

    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (!merchantId) return;

    setMarkingAsRegular(true);
    try {
      const transactionIds = Array.from(selectedIds)
        .map((idx) => txnList[idx]?.transactionId)
        .filter(Boolean)
        .join(",");
      const payload = {
        merchantId: parseInt(merchantId),
        transactionId: transactionIds,
        status: "UNMARKED",
      };

      const response = await MarkAsTestTransaction(payload);

      if (response.data.status === true) {
        showToast(
          "success",
          `Successfully marked ${selectedIds.size} transaction(s) as regular.`
        );
        clearSelection();
        fetchTransactions();
      } else {
        showToast(
          "error",
          response.data.message || "Failed to mark transactions as regular."
        );
      }
    } catch (error) {
      console.error("Mark as regular failed:", error);
      showToast(
        "error",
        "Failed to mark transactions as regular. Please try again."
      );
    } finally {
      setMarkingAsRegular(false);
    }
  };

  const handleRetryFailed = () => {
    console.log("Retry failed clicked", Array.from(selectedIds));
    showToast("info", "Retry functionality coming soon.");
  };

  // Refund modal helpers
  const fetchNetworkFees = (item, amountStr) => {
    if (!item) return;
    if (item.paymentMethod === "Brand Wallet") {
      setNetworkFee(0);
      return;
    }
    const amt = parseFloat(amountStr);
    if (!amt || amt <= 0) {
      setNetworkFee(0);
      return;
    }
    console.log("Fetching network fees with payload:", item);
    const userCountry =
      localStorage.getItem(SITENAMEALIAS + "_home_country") || "";
    const payload = {
      currencyId: item.currencyId,
      currency: item.currency,
      sendAmount: amt,
      marginType: 1,
      userCountry: userCountry,
      tokenType: (item.tokenType || "native").toLowerCase(),
    };
    setNetworkFeeLoading(true);
    get_network_fees(payload)
      .then((res) => {
        const d = res?.data || {};
        const fee =
          parseFloat(
            d.feesListResult?.[0]?.totalFees ??
              d.networkFee ??
              d.fee ??
              d.networkFeeAmount ??
              d.totalFee ??
              0
          ) || 0;
        setNetworkFee(fee);
      })
      .catch((err) => {
        console.error("Failed to fetch network fees:", err);
        setNetworkFee(0);
      })
      .finally(() => setNetworkFeeLoading(false));
  };

  const fetchWalletBalance = async (currency = "BTC") => {
    setWalletBalanceLoading(true);
    try {
      const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
      if (!merchantId) {
        setWalletBalance(0);
        return;
      }
      const res = await FetchUsdBtcLedgerAmount({ merchant_id: merchantId });
      const response = res?.data?.[0];
      if (response && response.error == "0") {
        const coinBalance = response.coin_balance || [];
        const match = coinBalance.find(
          (c) => c.currency_code?.toUpperCase() === currency.toUpperCase()
        );
        setWalletBalance(match ? parseFloat(match.balance) || 0 : 0);
      } else {
        setWalletBalance(0);
      }
    } catch (error) {
      console.error("Error in fetchWalletBalance:", error);
      setWalletBalance(0);
    } finally {
      setWalletBalanceLoading(false);
    }
  };

  const fetchRefundHistory = () => {
    setRefundHistoryLoading(true);
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    if (!merchantId) {
      setRefundHistoryLoading(false);
      return;
    }
    get_refund_list({
      merchantId: parseInt(merchantId),
      pageNo: "1",
      pageSize: "10",
    })
      .then((res) => {
        setRefundHistory(res.data?.refunds || []);
      })
      .catch((err) => {
        console.error("Failed to fetch refund history:", err);
        setRefundHistory([]);
      })
      .finally(() => setRefundHistoryLoading(false));
  };

  const openRefundModal = (t, e) => {
    e.stopPropagation();
    setRefundItem(t);
    setRefundAmount("");
    setRefundReason("Customer Request");
    setRefundNote("");
    setRefundError("");
    setNetworkFee(0);
    setRefundOpen(true);
    // Fetch merchant wallet balance for the transaction currency
    const currency = t.currency || "BTC";
    fetchWalletBalance(currency);
  };

  const closeRefundModal = () => {
    setRefundOpen(false);
    setRefundItem(null);
    setRefundError("");
  };

  const openRefundDetails = (refund) => {
    setSelectedRefund(refund);
    setRefundDetailsOpen(true);
  };

  const closeRefundDetails = () => {
    setRefundDetailsOpen(false);
    setSelectedRefund(null);
  };

  const isBrandWallet = refundItem?.paymentMethod === "Brand Wallet";
  const refundTotal =
    (parseFloat(refundAmount) || 0) + (isBrandWallet ? 0 : networkFee || 0);

  const handleIssueRefund = () => {
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) {
      setRefundError("Please enter a valid refund amount.");
      return;
    }
    // if (refundTotal > walletBalance) {
    //   setRefundError(
    //     "You do not have sufficient balance in your merchant wallet to process this refund amount."
    //   );
    //   return;
    // }
    const totalPaid = refundItem
      ? (refundItem.baseAmount || 0) + (refundItem.feeAmount || 0)
      : 0;
    if (amt > totalPaid) {
      setRefundError("Refund amount cannot exceed the total paid amount.");
      return;
    }

    setRefundError("");
    setRefundProcessing(true);

    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");

    const refundPayload = {
      transactionId: refundItem.transactionId,
      merchantId: parseInt(merchantId),
      refundAmount: amt,
      networkFeeAmount: isBrandWallet ? 0 : networkFee,
      reason: refundReason,
      note: refundNote,
    };

    create_refund(refundPayload)
      .then((res) => {
        if (res.data?.status === true) {
          if (isBrandWallet) {
            showToast("success", "Refund issued successfully.");
          } else {
            showToast(
              "success",
              res.data?.message ||
                "Refund issued successfully. Customer will receive an email to provide their refund address."
            );
          }

          closeRefundModal();
          fetchRefundHistory(); // Refresh refund list
          fetchTransactions(); // Refresh transaction list to show updated refund status
        } else {
          setRefundError(
            res.data?.message || "Failed to process refund. Please try again."
          );
        }
      })
      .catch((err) => {
        console.error("Refund processing error:", err);
        setRefundError("Failed to process refund. Please try again.");
      })
      .finally(() => setRefundProcessing(false));
  };

  const handleCancelSubscription = (t) => {
    const subId = t.subscriptionId;
    if (!subId || subId === "0") return;
    showConfirm(
      "Cancel Subscription",
      "Are you sure you want to cancel this subscription? This action cannot be undone.",
      "warning",
      () => {
        setCancelingId(t.transactionId);
        const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
        const payload = {
          subscriptionId: String(subId),
          merchantId: parseInt(merchantId),
          paymentType:
            t.paymentMethod === "PayBito Vertex"
              ? "netbilling"
              : t.paymentMethod || "Stripe",
          cancelAtPeriodEnd: false,
        };
        cancelSubscription(payload)
          .then((res) => {
            if (res.data && res.data.status === true) {
              showToast(
                "success",
                res.data.data || "Subscription cancelled successfully."
              );
              // Update drawer item immediately so status reflects without reload
              if (drawerItem && drawerItem.transactionId === t.transactionId) {
                setDrawerItem({
                  ...drawerItem,
                  subscriptionStatus: "cancelled",
                  status: "subscription_cancelled",
                });
              }
              fetchTransactions();
            } else {
              showToast(
                "error",
                res.data?.data || "Failed to cancel subscription."
              );
            }
          })
          .catch(() => {
            showToast(
              "error",
              "Failed to cancel subscription. Please try again."
            );
          })
          .finally(() => setCancelingId(null));
      }
    );
  };

  return (
    <Fragment>
      <style>{`
        /* Your existing styles here - keeping them as is */
        .tx-page{max-width:1200px;margin:0 auto;padding:32px 28px 60px}
        .main-panel{scrollbar-gutter:stable}
        .tx-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:28px;flex-wrap:wrap;gap:16px}
        .tx-title{font-size:22px;font-weight:700;letter-spacing:-.4px;margin:0;line-height:1.2}
        .tx-sub{font-size:13px;opacity:.45;margin-top:5px}
        .tx-hdr-right{display:flex;gap:10px;align-items:center}
        .tx-btn{height:36px;padding:0 16px;border-radius:8px;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .15s;white-space:nowrap;border:none;outline:none}
        .tx-btn-sec{background:var(--body-color-less);border:1px solid var(--body-color-less2);color:var(--text-color)}
        .tx-btn-sec:hover{border-color:rgba(99,102,241,.5);color:#818cf8}
        .tx-btn-sec:disabled{opacity:0.5;cursor:not-allowed}
        .tx-btn-ghost{background:transparent;border:1px solid var(--body-color-less2);color:var(--text-color);opacity:.6}
        .tx-btn-ghost:hover{opacity:1;border-color:rgba(99,102,241,.4);color:#818cf8}
        .tx-btn-ghost.active{opacity:1;border-color:rgba(99,102,241,.5);color:#818cf8;background:rgba(99,102,241,.06)}
        .tx-btn-primary{background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;box-shadow:0 0 14px rgba(99,102,241,.3);border:none}
        .tx-btn-primary:hover{filter:brightness(1.1)}
        .tx-btn-primary:disabled{opacity:0.5;cursor:not-allowed}
        .tx-btn-danger{background:transparent;border:1px solid rgba(239,68,68,.3);color:#f87171}
        .tx-btn-danger:hover{background:rgba(239,68,68,.08);border-color:rgba(239,68,68,.5)}
        .tx-btn-danger:disabled{opacity:0.5;cursor:not-allowed}
        .tx-btn-success{background:transparent;border:1px solid rgba(16,185,129,.3);color:#10b981}
        .tx-btn-success:hover{background:rgba(16,185,129,.08);border-color:rgba(16,185,129,.5)}
        .tx-ftabs{display:flex;gap:4px;flex-wrap:wrap;margin-bottom:14px}
        .tx-ftab{height:32px;padding:0 13px;border-radius:7px;border:1px solid transparent;background:transparent;font-family:inherit;font-size:12px;font-weight:600;color:var(--text-color);opacity:.5;cursor:pointer;transition:background .15s,border-color .15s,color .15s,opacity .15s;white-space:nowrap;display:flex;align-items:center;gap:5px}
        .tx-ftab:hover{background:rgba(255,255,255,.04);color:var(--text-color);opacity:.8}
        .tx-ftab.active{background:rgba(99,102,241,.12);border-color:rgba(99,102,241,.3);color:#818cf8;opacity:1}
        .tx-ftab-count{font-size:10px;background:rgba(99,102,241,.15);color:#818cf8;border-radius:10px;padding:1px 6px;font-weight:600}
        .tx-ftab.active .tx-ftab-count{background:rgba(99,102,241,.25)}
        .tx-frow{display:flex;align-items:center;gap:10px;margin-bottom:16px;flex-wrap:wrap}
        .tx-fsel{height:34px;padding:0 28px 0 11px;background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:8px;color:var(--text-color);font-family:inherit;font-size:12px;font-weight:500;appearance:none;-webkit-appearance:none;cursor:pointer;outline:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 8px center;transition:border-color .2s}
        .tx-fsel:focus{border-color:rgba(99,102,241,.5)}
        .tx-swrap{flex:1;min-width:220px;position:relative}
        .tx-sicon{position:absolute;left:11px;top:50%;transform:translateY(-50%);opacity:.35;pointer-events:none;font-size:14px}
        .tx-sinput{width:100%;height:34px;padding:0 12px 0 34px;background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:8px;color:var(--text-color);font-family:inherit;font-size:13px;outline:none;transition:border-color .2s,box-shadow .2s}
        .tx-sinput::placeholder{opacity:.4}
        .tx-sinput:focus{border-color:rgba(99,102,241,.5);box-shadow:0 0 0 3px rgba(99,102,241,.12)}
        .tx-date-range{display:flex;align-items:center;gap:8px}
        .tx-date-input{height:34px;padding:0 10px;background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:8px;color:var(--text-color);font-family:inherit;font-size:12px;outline:none;transition:border-color .2s}
        .tx-date-input:focus{border-color:rgba(99,102,241,.5)}
        .tx-date-sep{font-size:12px;opacity:.3;font-weight:600}
        .tx-mfp{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;padding:18px;margin-bottom:16px;display:none;animation:tx-fadeUp .2s ease}
        .tx-mfp.open{display:block}
        .tx-mfgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:12px}
        .tx-mff{display:flex;flex-direction:column;gap:5px}
        .tx-mflabel{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;opacity:.45}
        .tx-mfinput{height:34px;padding:0 10px;background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:7px;color:var(--text-color);font-family:inherit;font-size:12px;outline:none;transition:border-color .2s}
        .tx-mfinput::placeholder{opacity:.4}
        .tx-mfinput:focus{border-color:rgba(99,102,241,.5)}
        .tx-mfselect{height:34px;padding:0 28px 0 10px;background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:7px;color:var(--text-color);font-family:inherit;font-size:12px;appearance:none;-webkit-appearance:none;outline:none;cursor:pointer;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 7px center;transition:border-color .2s}
        .tx-mffoot{display:flex;justify-content:flex-end;gap:8px;margin-top:14px;padding-top:14px;border-top:1px solid var(--body-color-less2)}
        .tx-map-hint{font-size:10px;opacity:0.4;margin-top:2px}
        .tx-srow{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:22px}
        .tx-sc{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;padding:16px 18px;transition:border-color .3s}
        .tx-sc-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;opacity:.4;margin-bottom:8px}
        .tx-sc-val{font-size:22px;font-weight:700;letter-spacing:-.5px}
        .tx-sc-val.green{color:#10b981}
        .tx-sc-val.red{color:#ef4444}
        .tx-sc-val.yellow{color:#f59e0b}
        .tx-sc-delta{font-size:11px;opacity:.4;margin-top:4px}
        .tx-bulk{display:none;align-items:center;gap:10px;background:rgba(99,102,241,.06);border:1px solid rgba(99,102,241,.18);border-radius:10px;padding:12px 18px;margin-bottom:16px;animation:tx-fadeUp .2s ease;flex-wrap:wrap}
        .tx-bulk.show{display:flex}
        .tx-bulk-label{font-size:13px;font-weight:600;color:#818cf8;flex:1}
        .tx-tw{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;overflow:hidden}
        .tx-th{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid var(--body-color-less2)}
        .tx-ttl{font-size:12px;opacity:.4;font-weight:500}
        table.tx-tbl{width:100%;min-width:820px;border-collapse:collapse;table-layout:fixed}
        @media (max-width: 768px) {
          .tx-tw{overflow-x: visible!important; overflow-y: visible!important;}
          .tx-tbl-wrap{overflow-x: visible!important;overflow: visible !important;}
          table.tx-tbl{min-width: 0!important; width: 100%!important; table-layout: auto!important;}
          .tx-tbl thead{display: none;}
          .tx-tbl tbody tr{
            display: grid!important;
            grid-template-columns: auto 1fr auto auto;
            grid-template-rows: auto auto auto auto auto;
            gap: 2px 10px;
            padding: 12px 14px;
            border-bottom: 1px solid var(--body-color-less2);
            align-items: center;
          }
          .tx-tbl tbody tr:hover{background: rgba(99,102,241,.035);}
          .tx-tbl tbody td{display: block; padding: 0; font-size: 13px; overflow: visible; width: auto;}
          /* Row 1: checkbox | txn id | status | arrow */
          .tx-tbl tbody td:nth-child(1){grid-column:1; grid-row:1;}
          .tx-tbl tbody td:nth-child(2){grid-column:2; grid-row:1;}
          .tx-tbl tbody td:nth-child(7){grid-column:3; grid-row:1;}
          .tx-tbl tbody td:nth-child(9){grid-column:4; grid-row:1;}
          /* Row 2: product/type | amount */
          .tx-tbl tbody td:nth-child(3){grid-column:1 / 3; grid-row:2; padding-top:8px;}
          .tx-tbl tbody td:nth-child(4){grid-column:3 / 5; grid-row:2; text-align:right; padding-top:8px;}
          /* Row 3: method | date */
          .tx-tbl tbody td:nth-child(5){grid-column:1 / 3; grid-row:3; padding-top:4px;}
          .tx-tbl tbody td:nth-child(8){grid-column:3 / 5; grid-row:3; text-align:right; padding-top:4px;}
          /* Row 4: address (full width) */
          .tx-tbl tbody td:nth-child(6){grid-column:1 / -1; grid-row:4; font-size:11px; opacity:.5; padding-top:4px;}
          .tx-tbl .tx-av{font-size:13px;}
          .tx-tbl .tx-as{font-size:10px;}
          .tx-tbl .tx-cd{font-size:11px;}
        }
        .tx-tbl thead tr{border-bottom:1px solid var(--body-color-less2)}
        .tx-tbl thead th{padding:12px 16px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;opacity:.35;text-align:left;white-space:nowrap;user-select:none;cursor:default;transition:opacity .15s}
        .tx-tbl tbody tr{border-bottom:1px solid var(--body-color-less2);cursor:pointer;transition:background .15s}
        .tx-tbl tbody tr:last-child{border-bottom:none}
        .tx-tbl tbody tr:hover{background:rgba(99,102,241,.035)}
        .tx-tbl tbody tr.selected{background:rgba(99,102,241,.06)}
        .tx-tbl tbody td{padding:14px 16px;font-size:13px;vertical-align:middle;overflow:hidden}
        .tx-cn{font-weight:600;font-size:13px}
        .tx-ce{font-size:11px;opacity:.4;margin-top:2px}
        .tx-pn{font-weight:600;font-size:13px}
        .tx-av{font-weight:700;font-size:14px;font-family:'Plus Jakarta Sans',system-ui,sans-serif; overflow: visible !important;white-space: nowrap;}
        .tx-tbl tbody td {
  padding: 14px 16px;
  font-size: 13px;
  vertical-align: middle;
  overflow: visible !important; /* Change from hidden to visible */
}

/* Add this to ensure table row doesn't clip tooltips */
.tx-tbl tbody tr {
  border-bottom: 1px solid var(--body-color-less2);
  cursor: pointer;
  transition: background .15s;
  overflow: visible !important; /* Add this */
}
        .tx-as{font-size:11px;opacity:.4;margin-top:2px}
        .tx-mn{font-weight:500;font-size:13px}
        .tx-ms{font-size:11px;opacity:.4;margin-top:2px}
        .tx-cd{font-size:12px;opacity:.45;white-space:nowrap}
        .tx-badge{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:20px;font-size:11px;font-weight:600;white-space:nowrap}
        .tx-badge::before{content:'';width:6px;height:6px;border-radius:50%;flex-shrink:0}
        .tx-badge.succeeded{background:rgba(16,185,129,.1);color:#10b981}.tx-badge.succeeded::before{background:#10b981}
        .tx-badge.failed{background:rgba(239,68,68,.1);color:#f87171}.tx-badge.failed::before{background:#f87171}
        .tx-badge.cancelled{background:rgba(239,68,68,.1);color:#ef4444}.tx-badge.cancelled::before{background:#ef4444}
        .tx-badge.processing{background:rgba(245,158,11,.1);color:#f59e0b}.tx-badge.processing::before{background:#f59e0b}
        .tx-badge.pending{background:rgba(245,158,11,.1);color:#f59e0b}.tx-badge.pending::before{background:#f59e0b}
        .tx-badge.refunded{background:rgba(99,102,241,.1);color:#818cf8}.tx-badge.refunded::before{background:#818cf8}
        .tx-tag{display:inline-block;padding:2px 8px;border-radius:5px;font-size:10px;font-weight:600;margin-top:3px}
        .tx-tag.sub{background:rgba(139,92,246,.1);color:#a78bfa;border:1px solid rgba(139,92,246,.15)}
        .tx-tag.onetime{background:rgba(59,130,246,.08);color:#60a5fa;border:1px solid rgba(59,130,246,.15)}
        .tx-tag.test{background:rgba(100,116,139,.1);opacity:.55;border:1px solid rgba(100,116,139,.15)}
        .tx-tag.crypto{background:rgba(6,182,212,.08);color:#22d3ee;border:1px solid rgba(6,182,212,.15)}
        .tx-arw{width:28px;height:28px;border-radius:7px;background:rgba(99,102,241,.08);border:1px solid rgba(99,102,241,.15);color:#818cf8;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .18s}
        .tx-arw:hover{background:rgba(99,102,241,.18);border-color:rgba(99,102,241,.3)}
        .tx-rcb{accent-color:#6366f1;width:15px;height:15px;cursor:pointer}
        .tx-loading{display:flex;align-items:center;justify-content:center;padding:60px 20px;gap:10px;opacity:.45;font-size:13px}
        .tx-empty{display:flex;flex-direction:column;align-items:center;padding:70px 20px;gap:14px}
        .tx-empty-icon{font-size:36px;opacity:.15}
        .tx-empty-t{font-size:15px;font-weight:600;opacity:.7}
        .tx-empty-s{font-size:13px;opacity:.35}
        .tx-pag{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-top:1px solid var(--body-color-less2)}
        .tx-pi{font-size:12px;opacity:.35;font-weight:500}
        .tx-pbs{display:flex;gap:6px;align-items:center}
        .tx-pb{height:30px;min-width:30px;padding:0 8px;border-radius:7px;border:1px solid var(--body-color-less2);background:transparent;color:var(--text-color);font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;opacity:.55}
        .tx-pb:hover:not(:disabled){border-color:rgba(99,102,241,.4);color:#818cf8;opacity:1}
        .tx-pb.active{background:rgba(99,102,241,.1);border-color:rgba(99,102,241,.25);color:#818cf8;font-weight:600;opacity:1}
        .tx-pb:disabled{opacity:.25;cursor:not-allowed}
        .tx-dov{position:fixed;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(4px);z-index:1050;opacity:0;pointer-events:none;transition:opacity .25s}
        .tx-dov.open{opacity:1;pointer-events:all}
        .tx-dr{position:fixed;top:0;right:0;bottom:0;width:430px;max-width:95vw;background:var(--body-color-less);border-left:1px solid var(--body-color-less2);z-index:1060;overflow-y:auto;transform:translateX(100%);transition:transform .3s cubic-bezier(.4,0,.2,1);padding:28px}
        .tx-dr.open{transform:translateX(0)}
        .tx-drh{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px}
        .tx-drt{font-size:17px;font-weight:700}
        .tx-drc{width:32px;height:32px;border-radius:8px;background:var(--body-color-less2);border:1px solid var(--body-color-less2);color:var(--text-color);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:16px;opacity:.5;transition:all .18s}
        .tx-drc:hover{opacity:1;background:rgba(99,102,241,.1)}
        .tx-dr-mobile-close{display:none}
        .tx-ds{margin-bottom:22px;padding-bottom:22px;border-bottom:1px solid var(--body-color-less2)}
        .tx-ds:last-child{border-bottom:none;margin-bottom:0;padding-bottom:0}
        .tx-dst{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;opacity:.3;margin-bottom:14px}
        .tx-dr-row{display:flex;justify-content:space-between;align-items:flex-start;padding:6px 0;gap:12px}
        .tx-dk{font-size:12px;opacity:.4;flex-shrink:0}
        .tx-dv{font-size:13px;font-weight:500;text-align:right;word-break:break-all}
        .tx-dv.mono{font-family:monospace;font-size:11px;color:#818cf8}
        .tx-dv.green{color:#10b981}
        .tx-dv.red{color:#ef4444}
        .tx-tl{display:flex;flex-direction:column;gap:0}
        .tx-tli{display:flex;gap:12px;padding-bottom:16px;position:relative}
        .tx-tli:last-child{padding-bottom:0}
        .tx-tli:not(:last-child)::before{content:'';position:absolute;left:7px;top:18px;width:2px;bottom:0;background:var(--body-color-less2)}
        .tx-tld{width:16px;height:16px;border-radius:50%;flex-shrink:0;margin-top:2px;border:2px solid}
        .tx-tld.done{background:#10b981;border-color:#10b981}
        .tx-tld.current{background:#6366f1;border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,.2)}
        .tx-tld.pnd{background:transparent;border-color:var(--body-color-less2)}
        .tx-tld.fail{background:#ef4444;border-color:#ef4444}
        .tx-tll{font-size:13px;font-weight:600}
        .tx-tlt{font-size:11px;opacity:.4;margin-top:2px}
        .tx-brow{display:flex;justify-content:space-between;padding:6px 0;font-size:13px}
        .tx-brow.total{font-weight:700;border-top:1px solid var(--body-color-less2);margin-top:6px;padding-top:10px}
        .tx-ftab.test-user{border-color:rgba(16,185,129,.3)}
        .tx-ftab.test-user.active{background:rgba(16,185,129,.12);border-color:rgba(16,185,129,.3);color:#10b981}
        .tx-ftab.test-user.active .tx-ftab-count{background:rgba(16,185,129,.25);color:#10b981}
        .tx-cancel-btn{display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;border:1px solid rgba(239,68,68,.3);background:rgba(239,68,68,.06);color:#f87171;transition:all .15s;white-space:nowrap;margin-top:4px}
        .tx-cancel-btn:hover{background:rgba(239,68,68,.12);border-color:rgba(239,68,68,.5)}
        .tx-cancel-btn:disabled{opacity:0.5;cursor:not-allowed}
        .tx-trial-info{font-size:10px;color:#f59e0b;margin-top:2px;font-weight:500}
        .tx-refund-btn{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;border:1px solid rgba(99,102,241,.3);background:rgba(99,102,241,.06);color:#818cf8;transition:all .15s;white-space:nowrap;margin-top:5px;font-family:inherit}
        .tx-refund-btn:hover{background:rgba(99,102,241,.14);border-color:rgba(99,102,241,.5)}
        .tx-modal-ov{position:fixed;inset:0;background:rgba(0,0,0,.6);backdrop-filter:blur(6px);z-index:1060;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;transition:opacity .25s}
        .tx-modal-ov.open{opacity:1;pointer-events:all}
        .tx-modal{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:16px;width:480px;max-width:94vw;max-height:85vh;overflow-y:auto;padding:0;box-shadow:0 24px 80px rgba(0,0,0,.4);transform:scale(.95) translateY(10px);transition:transform .3s cubic-bezier(.4,0,.2,1);animation:tx-modalIn .3s ease forwards;margin-top:20px}
        .tx-modal-ov.open .tx-modal{transform:scale(1) translateY(0)}
        @keyframes tx-modalIn{from{opacity:0;transform:scale(.95) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
        .tx-modal-hdr{display:flex;align-items:center;justify-content:space-between;padding:22px 28px 18px;border-bottom:1px solid var(--body-color-less2)}
        .tx-modal-title{font-size:18px;font-weight:700;display:flex;align-items:center;gap:10px}
        .tx-modal-title i{font-size:22px;color:#818cf8}
        .tx-modal-close{width:32px;height:32px;border-radius:8px;background:var(--body-color-less2);border:1px solid var(--body-color-less2);color:var(--text-color);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:16px;opacity:.5;transition:all .18s}
        .tx-modal-close:hover{opacity:1;background:rgba(239,68,68,.1);color:#f87171}
        .tx-modal-body{padding:24px 28px}
        .tx-modal-row{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid rgba(255,255,255,.04)}
        .tx-modal-row:last-child{border-bottom:none}
        .tx-modal-lbl{font-size:13px;opacity:.5;font-weight:500}
        .tx-modal-val{font-size:14px;font-weight:600;text-align:right}
        .tx-modal-val.mono{font-family:monospace;color:#818cf8}
        .tx-modal-val.highlight{color:#10b981;font-size:16px}
        .tx-modal-divider{height:1px;background:var(--body-color-less2);margin:18px 0}
        .tx-modal-field{margin-bottom:18px}
        .tx-modal-field label{display:block;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;opacity:.4;margin-bottom:8px}
        .tx-modal-input{width:100%;height:42px;padding:0 14px;background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:10px;color:var(--text-color);font-family:inherit;font-size:14px;font-weight:600;outline:none;transition:border-color .2s,box-shadow .2s;box-sizing:border-box}
        .tx-modal-input:focus{border-color:rgba(99,102,241,.5);box-shadow:0 0 0 3px rgba(99,102,241,.12)}
        .tx-modal-select{width:100%;height:42px;padding:0 32px 0 14px;background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:10px;color:var(--text-color);font-family:inherit;font-size:13px;font-weight:500;outline:none;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;transition:border-color .2s;box-sizing:border-box}
        .tx-modal-select:focus{border-color:rgba(99,102,241,.5)}
        .tx-modal-textarea{width:100%;height:70px;padding:10px 14px;background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:10px;color:var(--text-color);font-family:inherit;font-size:13px;outline:none;resize:vertical;transition:border-color .2s;box-sizing:border-box}
        .tx-modal-textarea:focus{border-color:rgba(99,102,241,.5)}
        .tx-modal-summary{background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:12px;padding:16px 18px;margin-bottom:18px}
        .tx-modal-summary-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px}
        .tx-modal-summary-row.total{font-weight:700;font-size:14px;border-top:1px solid var(--body-color-less2);margin-top:8px;padding-top:10px}
        .tx-modal-wallet{display:flex;align-items:center;gap:8px;background:rgba(16,185,129,.06);border:1px solid rgba(16,185,129,.15);border-radius:10px;padding:12px 16px;margin-bottom:18px}
        .tx-modal-wallet i{font-size:20px;color:#10b981}
        .tx-modal-wallet-info{flex:1}
        .tx-modal-wallet-lbl{font-size:11px;opacity:.5;font-weight:600}
        .tx-modal-wallet-val{font-size:15px;font-weight:700;color:#10b981}
        .tx-modal-error{background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.2);border-radius:10px;padding:12px 16px;margin-bottom:18px;font-size:12px;color:#f87171;font-weight:500;display:flex;align-items:flex-start;gap:8px}
        .tx-modal-error i{font-size:16px;flex-shrink:0;margin-top:1px}
        .tx-modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:18px 28px 22px;border-top:1px solid var(--body-color-less2)}
        .tx-modal-btn-cancel{height:40px;padding:0 22px;border-radius:10px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;border:1px solid var(--body-color-less2);background:transparent;color:var(--text-color);opacity:.7;transition:all .15s}
        .tx-modal-btn-cancel:hover{opacity:1;border-color:rgba(255,255,255,.15)}
        .tx-modal-btn-refund{height:40px;padding:0 24px;border-radius:10px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;border:none;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;box-shadow:0 0 20px rgba(99,102,241,.3);transition:all .15s;display:flex;align-items:center;gap:7px}
        .tx-modal-btn-refund:hover{filter:brightness(1.1);box-shadow:0 0 28px rgba(99,102,241,.4)}
        .tx-modal-btn-refund:disabled{opacity:.5;cursor:not-allowed;filter:none}
        .tx-table{width:100%;border-collapse:collapse;table-layout:auto}
        .tx-table thead{background:rgba(99,102,241,.04);border-bottom:1px solid var(--body-color-less2)}
        .tx-table thead th{padding:14px 16px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;opacity:.4;text-align:left;white-space:nowrap}
        .tx-table tbody tr{border-bottom:1px solid var(--body-color-less2);transition:background .15s}
        .tx-table tbody tr:last-child{border-bottom:none}
        .tx-table tbody tr:hover{background:rgba(99,102,241,.03)}
        .tx-table tbody td{padding:14px 16px;font-size:13px;vertical-align:middle}
        .tx-table .mono{font-family:monospace;color:#818cf8;font-size:12px}
        .tx-action-btn{padding:6px 12px;border-radius:6px;border:none;font-size:12px;cursor:pointer;font-weight:500;transition:all .15s}
        .tx-tag.success{background:rgba(16,185,129,.1);color:#10b981;border:1px solid rgba(16,185,129,.2)}
        .tx-tag.processing{background:rgba(245,158,11,.1);color:#f59e0b;border:1px solid rgba(245,158,11,.2)}
        .tx-tag.pending{background:rgba(245,158,11,.1);color:#f59e0b;border:1px solid rgba(245,158,11,.2)}
        @keyframes tx-fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .tx-reward-icon{position:relative;display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;font-size:11px;cursor:default;vertical-align:middle;margin-left:4px;z-index: 1;}
        .tx-reward-icon:hover .tx-reward-tip{opacity:1;pointer-events:all;transform:translateY(-2px)}
        .tx-reward-tip{position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%) translateY(0);background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:8px;padding:6px 10px;font-size:11px;font-weight:600;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .18s,transform .18s;z-index:9999999;box-shadow:0 4px 16px rgba(0,0,0,.3);min-width:160px;left:auto;right:0;transform:translateY(0)}
        .tx-reward-tip::after{content:'';position:absolute;top:100%;right:6px;border:5px solid transparent;border-top-color:var(--body-color-less2)}
        .tx-reward-tip-row{display:flex;justify-content:space-between;gap:12px;padding:2px 0}
        .tx-reward-tip-label{opacity:.5;font-weight:500}
        .tx-reward-tip-val{font-weight:700}
        /* ===== Tablet ===== */
        @media (max-width: 991.98px) {
          .tx-page{padding: 20px 16px 40px;}
          .tx-hdr{flex-direction: column; gap: 12px;}
          .tx-hdr-right{width: 100%; flex-wrap: wrap;}
          .tx-title{font-size: 18px;}
          .tx-srow{grid-template-columns: 1fr 1fr;}
          div[style*="overflowX: auto"]{overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; width: 100%;}
          .tx-table{width: 100%; min-width: auto;}
          .tx-table thead th{padding: 12px 14px; font-size: 10px;}
          .tx-table tbody td{padding: 12px 14px; font-size: 12px;}
          .tx-btn{height: 32px; font-size: 12px; padding: 0 12px;}
          .tx-ftabs{gap: 3px;}
          .tx-ftab{height: 28px; padding: 0 10px; font-size: 11px;}
          .tx-fsel{height: 32px; font-size: 11px;}
          .tx-sinput{height: 32px; font-size: 12px;}
          .tx-date-input{height: 32px; font-size: 11px;}
          .tx-frow{gap: 8px;}
          .tx-swrap{min-width: 180px;}
          .tx-pag{flex-direction: column; gap: 12px; align-items: center;}
          .tx-pbs{flex-wrap: wrap; justify-content: center;}
          .tx-sc{padding: 14px 14px;}
          .tx-sc-val{font-size: 18px;}
          .tx-bulk{gap: 8px;}
          .tx-bulk-label{flex: 1 1 100%; font-size: 12px;}
        }
        /* ===== Mobile ===== */
        @media (max-width: 576px) {
          .tx-page{padding: 16px 12px 30px; max-width: 100vw; box-sizing: border-box;}
          .tx-title{font-size: 16px;}
          .tx-hdr-right{width: 100%; flex-direction: row;}
          .tx-btn{height: 30px; font-size: 11px; padding: 0 10px;}
          .tx-table thead th{padding: 10px 12px; font-size: 9px;}
          .tx-table tbody td{padding: 10px 12px; font-size: 11px;}
          .tx-srow{grid-template-columns: 1fr 1fr; gap: 10px;}
          .tx-sc{padding: 12px 14px; overflow: hidden;}
          .tx-sc-val{font-size: 16px; word-break: break-all;}
          .tx-sc-label{font-size: 10px; margin-bottom: 6px;}
          .tx-sc-delta{font-size: 10px;}
          .tx-frow{flex-direction: column; align-items: stretch;}
          .tx-fsel{width: 100%; height: 36px; font-size: 12px; box-sizing: border-box;}
          .tx-swrap{min-width: 0; width: 100%;}
          .tx-sinput{height: 36px; font-size: 12px;}
          .tx-date-input{width: 100%; height: 34px; font-size: 11px;}
          .tx-date-range{flex-direction: column; gap: 6px; width: 100%;}
          .tx-ftabs{gap: 4px; overflow-x: auto; flex-wrap: nowrap; padding-bottom: 4px; -webkit-overflow-scrolling: touch; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -12px; padding-left: 12px; padding-right: 12px;}
          .tx-ftabs::-webkit-scrollbar{display: none;}
          .tx-ftab{flex-shrink: 0; height: 30px; padding: 0 10px; font-size: 11px;}
          .tx-th{flex-direction: column; gap: 8px; align-items: flex-start; padding: 12px 14px;}
          .tx-pag{flex-direction: column; gap: 10px; align-items: center; padding: 12px 14px;}
          .tx-pbs{flex-wrap: wrap; justify-content: center; gap: 4px;}
          .tx-pb{height: 28px; min-width: 28px; font-size: 11px;}
          .tx-mfgrid{grid-template-columns: 1fr;}
          .tx-dr{width: 100%; max-width: 100vw; padding: 20px 16px 20px;}
          .tx-dr-mobile-close{display:flex!important;position:fixed;top:12px;right:12px;z-index:1100;width:40px;height:40px;border-radius:50%;background:var(--body-color-less2);border:1px solid var(--body-color-less2);color:var(--text-color);cursor:pointer;align-items:center;justify-content:center;font-size:20px;box-shadow:0 2px 12px rgba(0,0,0,.4);}
          .tx-modal{max-width: 96vw; margin: 10px;}
          .tx-modal-body{padding: 18px 16px;}
          .tx-modal-hdr{padding: 16px 16px 14px;}
          .tx-modal-footer{padding: 14px 16px 16px; flex-direction: column;}
          .tx-modal-footer button{width: 100%;}
          .tx-bulk{padding: 10px 14px;}
          .tx-bulk .tx-btn{font-size: 10px; padding: 0 8px; height: 28px;}
        }
      `}</style>

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="tx-page">
            {/* ── Header ── */}
            <div className="tx-hdr">
              <div>
                <h1 className="tx-title">Transactions</h1>
                <p className="tx-sub">View and manage all payments</p>
              </div>
              <div className="tx-hdr-right">
                <button
                  className="tx-btn tx-btn-sec"
                  onClick={handleExportCSV}
                  disabled={exporting || currentPageAccess() === "READ"}
                  style={
                    currentPageAccess() === "READ"
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : null
                  }
                >
                  <i className="mdi mdi-download" style={{ fontSize: 15 }}></i>
                  {exporting ? "Exporting..." : "Export CSV"}
                </button>
              </div>
            </div>

            {/* ── Filter Tabs ── */}
            <div className="tx-ftabs">
              {filterDefs.map((f) => (
                <button
                  key={f.key}
                  className={`tx-ftab ${
                    activeFilter === f.key ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange(f.key)}
                >
                  {f.label}
                  {/* <span className="tx-ftab-count">{f.count}</span> */}
                </button>
              ))}
              <button
                className={`tx-ftab ${showTestOnly ? "active" : ""}`}
                onClick={handleTestUserFilter}
              >
                <i
                  className="mdi mdi-account-check"
                  style={{ fontSize: 14, marginRight: 4 }}
                ></i>
                Test User{" "}
                {/* {showTestOnly && (
                  <span className="tx-ftab-count">{summary.totalCount}</span>
                )} */}
              </button>
            </div>

            {/* ── Filters Row ── */}
            <div className="tx-frow">
              <select
                className="tx-fsel"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="ALL">Date: All time</option>
                <option value="today">Today</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="month">This month</option>
                <option value="custom">Custom range</option>
              </select>
              {dateFilter === "custom" && (
                <div className="tx-date-range">
                  <input
                    type="date"
                    className="tx-date-input"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                  />
                  <span className="tx-date-sep">to</span>
                  <input
                    type="date"
                    className="tx-date-input"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                  />
                </div>
              )}
              <select
                className="tx-fsel"
                value={methodFilter}
                onChange={(e) => {
                  setMethodFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">ALL Payment Methods</option>
                <option value="STRIPE">Stripe</option>
                <option value="PAYPAL">PayPal</option>
                <option value="Brand Wallet">Brand Wallet</option>
                <option value="Guest Checkout">Guest Checkout</option>
              </select>
              <select
                className="tx-fsel"
                value={networkFilter}
                onChange={(e) => {
                  setNetworkFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Network</option>
                <option value="NATIVE">Native</option>
                <option value="ERC">ERC</option>
                <option value="TRC">TRC</option>
              </select>
              <select
                className="tx-fsel"
                value={currencyFilter}
                onChange={(e) => {
                  setCurrencyFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Currency</option>
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
                <option value="LTC">LTC</option>
                <option value="BCH">BCH</option>
                <option value="USDT">USDT</option>
                <option value="USDC">USDC</option>
                <option value="USD">USD</option>
                <option value="XRP">XRP</option>
                <option value="DOGE">DOGE</option>
                <option value="HCX">HCX</option>
              </select>
              <button
                className={`tx-btn tx-btn-ghost ${
                  moreFiltersOpen ? "active" : ""
                }`}
                onClick={() => setMoreFiltersOpen(!moreFiltersOpen)}
              >
                <i className="mdi mdi-tune" style={{ fontSize: 15 }}></i> More
                Filters
              </button>
              <div className="tx-swrap">
                <i className="mdi mdi-magnify tx-sicon"></i>
                <input
                  className="tx-sinput"
                  placeholder="Search by email, transaction ID..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                />
              </div>
            </div>

            {/* ── More Filters Panel ── */}
            <div className={`tx-mfp ${moreFiltersOpen ? "open" : ""}`}>
              <div className="tx-mfgrid">
                <div className="tx-mff">
                  <span className="tx-mflabel">Product Name</span>
                  <input
                    className="tx-mfinput"
                    placeholder="Product name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                  />
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Catalogue Name</span>
                  <input
                    className="tx-mfinput"
                    placeholder="Catalogue name"
                    value={catalogueName}
                    onChange={(e) => setCatalogueName(e.target.value)}
                  />
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Subscription ID</span>
                  <input
                    className="tx-mfinput"
                    placeholder="sub_..."
                    value={subscriptionId}
                    onChange={(e) => setSubscriptionId(e.target.value)}
                  />
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Customer ID</span>
                  <input
                    className="tx-mfinput"
                    placeholder="cus_..."
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                  />
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Customer Identity</span>
                  <select
                    className="tx-mfselect"
                    value={customerIdentity}
                    onChange={(e) => setCustomerIdentity(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="GUEST">Guest</option>
                    <option value="PAYMENT_LINK">Payment Link</option>
                    <option value="VERIFIED">Verified</option>
                  </select>
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Gateway</span>
                  <select
                    className="tx-mfselect"
                    value={gatewayFilter}
                    onChange={(e) => setGatewayFilter(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="STRIPE">Stripe</option>
                    <option value="PAYPAL">PayPal</option>
                    <option value="Brand Wallet">Brand Wallet</option>
                    <option value="Guest Checkout">Guest Checkout</option>
                  </select>
                  <div className="tx-map-hint">→ paymentMethod</div>
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Payment Type</span>
                  <select
                    className="tx-mfselect"
                    value={paymentTypeFilter}
                    onChange={(e) => setPaymentTypeFilter(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="ONE-TIME">One-time</option>
                    <option value="RECURRING">Recurring</option>
                  </select>
                  <div className="tx-map-hint">→ paymentType</div>
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Blockchain</span>
                  <select
                    className="tx-mfselect"
                    value={blockchainFilter}
                    onChange={(e) => setBlockchainFilter(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="NATIVE">Native</option>
                    <option value="ERC">ERC</option>
                    <option value="TRC">TRC</option>
                  </select>
                  <div className="tx-map-hint">→ network</div>
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Asset</span>
                  <select
                    className="tx-mfselect"
                    value={assetFilter}
                    onChange={(e) => setAssetFilter(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="USDT">USDT</option>
                    <option value="USDC">USDC</option>
                    <option value="ETH">ETH</option>
                    <option value="BTC">BTC</option>
                    <option value="LTC">LTC</option>
                    <option value="BCH">BCH</option>
                    <option value="XRP">XRP</option>
                    <option value="USD">USD</option>
                  </select>
                  <div className="tx-map-hint">→ currency</div>
                </div>
                <div className="tx-mff">
                  <span className="tx-mflabel">Reward</span>
                  <select
                    className="tx-mfselect"
                    value={rewardFilter}
                    onChange={(e) => setRewardFilter(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="1">Store Credit Earned</option>
                    <option value="2">Store Credit Redeemed</option>
                    <option value="3">Cashback Earned</option>
                  </select>
                  <div className="tx-map-hint">→ reward</div>
                </div>
              </div>
              <div className="tx-mffoot">
                <button
                  className="tx-btn tx-btn-ghost"
                  onClick={clearMoreFilters}
                >
                  Clear All
                </button>
                <button
                  className="tx-btn tx-btn-primary"
                  onClick={applyMoreFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>

            {/* ── Summary Cards ── */}
            <div className="tx-srow">
              <div className="tx-sc">
                <div className="tx-sc-label">Total Volume</div>
                <div className="tx-sc-val">
                  {fmtAmt(summary.totalVolume, "USD")}
                </div>
                <div className="tx-sc-delta">
                  {summary.totalCount} transaction
                  {summary.totalCount !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="tx-sc">
                <div className="tx-sc-label">Successful</div>
                <div className="tx-sc-val green">
                  {fmtAmt(summary.successVolume, "USD")}
                </div>
                <div className="tx-sc-delta">
                  {summary.succeededCount} payment
                  {summary.succeededCount !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="tx-sc">
                <div className="tx-sc-label">Failed</div>
                <div className="tx-sc-val red">
                  {fmtAmt(summary.failedVolume, "USD")}
                </div>
                <div className="tx-sc-delta">
                  {summary.failedCount} payment
                  {summary.failedCount !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="tx-sc">
                <div className="tx-sc-label">Refunded</div>
                <div className="tx-sc-val yellow">
                  {fmtAmt(summary.refundedVolume, "USD")}
                </div>
                <div className="tx-sc-delta">
                  {summary.refundedCount} payment
                  {summary.refundedCount !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {/* ── Bulk Actions Bar ── */}
            <div className={`tx-bulk ${selectedIds.size > 0 ? "show" : ""}`}>
              <span className="tx-bulk-label">{selectedIds.size} selected</span>
              {showTestOnly ? (
                <button
                  className="tx-btn tx-btn-success"
                  onClick={handleMarkAsRegular}
                  disabled={markingAsRegular}
                >
                  <i
                    className="mdi mdi-account-check"
                    style={{ fontSize: 14 }}
                  ></i>
                  {markingAsRegular ? "Marking..." : "Mark as Regular"}
                </button>
              ) : (
                <button
                  className="tx-btn tx-btn-success"
                  onClick={handleMarkAsTest}
                  disabled={markingAsTest}
                >
                  <i className="mdi mdi-test-tube" style={{ fontSize: 14 }}></i>
                  {markingAsTest ? "Marking..." : "Mark as Test"}
                </button>
              )}
              <button
                className="tx-btn tx-btn-ghost"
                onClick={clearSelection}
                style={{ opacity: 1, marginLeft: "auto" }}
              >
                <i className="mdi mdi-close" style={{ fontSize: 14 }}></i> Clear
              </button>
            </div>

            {/* ── Table ── */}
            <div className="tx-tw" ref={tableRef}>
              <div className="tx-th">
                <span className="tx-ttl">
                  {loading
                    ? "Loading..."
                    : `Showing ${summary.totalRecords} transaction${
                        summary.totalRecords !== 1 ? "s" : ""
                      }`}
                </span>
                <select
                  className="tx-fsel"
                  style={{ height: 30, fontSize: 11 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="10">10 / page</option>
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                </select>
              </div>
              <div className="tx-tbl-wrap" style={{ overflowX: "auto" }}>
                <table className="tx-tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}>
                        <input
                          type="checkbox"
                          className="tx-rcb"
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th style={{ width: 60 }}>Txn ID</th>
                      <th style={{ width: 110 }}>Product / Type</th>
                      <th style={{ width: 150 }}>Amount</th>
                      <th style={{ width: 80 }}>Method</th>

                      <th style={{ width: 80 }}>Payment type</th>
                      <th style={{ width: 255 }}>Status</th>
                      <th style={{ width: 90 }}>Date</th>
                      <th style={{ width: 45 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading &&
                      txnList.map((t, idx) => {
                        const sel = selectedIds.has(idx);
                        const status = mapStatus(t.status);
                        const displayProductName =
                          t.productDetails && t.productDetails.length > 0
                            ? t.productDetails[0].productName
                            : t.productName || "—";
                        const paymentType = t.paymentType || "ONE-TIME";
                        const blockchain =
                          t.paymentSubDetails || t.network || "—";
                        const transactionAddress =
                          t.sessionId || t.transactionAddress || "—";
                        const method = t.paymentMethod;

                        return (
                          <tr
                            key={idx}
                            className={sel ? "selected" : ""}
                            onClick={() => openDrawer(t)}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="tx-rcb"
                                checked={sel}
                                onChange={() => toggleSelect(idx)}
                              />
                            </td>
                            <td>
                              <div
                                className="tx-cn"
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: 12,
                                }}
                              >
                                #{t.transactionId}
                              </div>
                              {t.testMode === true && (
                                <span className="tx-tag test">TEST</span>
                              )}
                            </td>
                            <td>
                              <div className="tx-pn">{displayProductName}</div>
                              <span
                                className={`tx-tag ${
                                  paymentType === "SUBSCRIPTION"
                                    ? "sub"
                                    : "onetime"
                                }`}
                              >
                                {paymentType}
                              </span>
                            </td>
                            <td>
                              <div
                                className="tx-av"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 4,
                                }}
                              >
                                <span>
                                  {fmtAmt(
                                    t.baseAmount + t.feeAmount,
                                    t.currency
                                  )}
                                </span>

                                {/* Store Credit Earned Icon */}
                                {t.storeCrEarned > 0 && (
                                  <span
                                    className="tx-reward-icon"
                                    style={{
                                      background: "rgba(16,185,129,.15)",
                                      color: "#10b981",
                                    }}
                                    title=""
                                  >
                                    <i
                                      className="mdi mdi-arrow-down-bold-circle-outline"
                                      style={{ fontSize: 11 }}
                                    ></i>
                                    <span className="tx-reward-tip">
                                      <div className="tx-reward-tip-row">
                                        <span className="tx-reward-tip-label">
                                          SC Earned
                                        </span>
                                        <span
                                          className="tx-reward-tip-val"
                                          style={{ color: "#10b981" }}
                                        >
                                          {fmtAmt(t.storeCrEarned, t.currency)}
                                        </span>
                                      </div>
                                    </span>
                                  </span>
                                )}

                                {/* Store Credit Redeemed Icon */}
                                {t.storeCrRedeemed > 0 && (
                                  <span
                                    className="tx-reward-icon"
                                    style={{
                                      background: "rgba(139,92,246,.15)",
                                      color: "#a78bfa",
                                    }}
                                    title=""
                                  >
                                    <i
                                      className="mdi mdi-arrow-up-bold-circle-outline"
                                      style={{ fontSize: 11 }}
                                    ></i>
                                    <span className="tx-reward-tip">
                                      <div className="tx-reward-tip-row">
                                        <span className="tx-reward-tip-label">
                                          SC Redeemed
                                        </span>
                                        <span
                                          className="tx-reward-tip-val"
                                          style={{ color: "#a78bfa" }}
                                        >
                                          {fmtAmt(
                                            t.storeCrRedeemed,
                                            t.currency
                                          )}
                                        </span>
                                      </div>
                                    </span>
                                  </span>
                                )}

                                {/* Cashback Icon */}
                                {t.cashbackEarned > 0 && (
                                  <span
                                    className="tx-reward-icon"
                                    style={{
                                      background: "rgba(245,158,11,.15)",
                                      color: "#f59e0b",
                                    }}
                                    title=""
                                  >
                                    <i
                                      className="mdi mdi-cash-refund"
                                      style={{ fontSize: 11 }}
                                    ></i>
                                    <span className="tx-reward-tip">
                                      <div className="tx-reward-tip-row">
                                        <span className="tx-reward-tip-label">
                                          Cashback
                                        </span>
                                        <span
                                          className="tx-reward-tip-val"
                                          style={{ color: "#f59e0b" }}
                                        >
                                          {fmtAmt(t.cashbackEarned, t.currency)}
                                        </span>
                                      </div>
                                    </span>
                                  </span>
                                )}

                                {/* Cashback Redeemed Icon */}
                                {t.cashbackRedeemed > 0 && (
                                  <span
                                    className="tx-reward-icon"
                                    style={{
                                      background: "rgba(239,68,68,.15)",
                                      color: "#f87171",
                                    }}
                                    title=""
                                  >
                                    <i
                                      className="mdi mdi-cash"
                                      style={{ fontSize: 11 }}
                                    ></i>
                                    <span className="tx-reward-tip">
                                      <div className="tx-reward-tip-row">
                                        <span className="tx-reward-tip-label">
                                          Cashback Redeemed
                                        </span>
                                        <span
                                          className="tx-reward-tip-val"
                                          style={{ color: "#f87171" }}
                                        >
                                          {fmtAmt(t.cashbackRedeemed, t.currency)}
                                        </span>
                                      </div>
                                    </span>
                                  </span>
                                )}
                              </div>
                              <div className="tx-as">
                                Base: {fmtAmt(t.baseAmount, t.currency)} | Fee:{" "}
                                {fmtAmt(t.feeAmount, t.currency)}
                              </div>
                            </td>
                            <td>
                              <div className="tx-pn">{method}</div>
                            </td>
                            <td>
                              {blockchain !== "—" ? (
                                <>
                                  <div className="tx-mn">{blockchain}</div>
                                  {/* <div
                                    className="tx-ms"
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: 10,
                                    }}
                                  >
                                    {transactionAddress !== "—"
                                      ? transactionAddress.slice(0, 18) + "…"
                                      : "—"}
                                  </div> */}
                                </>
                              ) : (
                                <span style={{ opacity: 0.3 }}>—</span>
                              )}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <span className={`tx-badge ${status}`}>
                                {statusLabel(t.status)}
                              </span>
                              {status === "succeeded" &&
                                (t.paymentMethod === "PayPal" ||
                                  t.paymentMethod === "Stripe") && (
                                  <span
                                    className="tx-badge succeeded"
                                    style={{ marginLeft: 6 }}
                                  >
                                    Refund via {t.paymentMethod}
                                  </span>
                                )}
                              {/* status === "succeeded" */}
                              {status === "succeeded" &&
                                [
                                  "Guest Checkout",
                                  "Brand Wallet",
                                  "External Wallet",
                                ].includes(t.paymentMethod) &&
                                t.paymentSubDetails &&
                                t.paymentSubDetails !== "Fiat" &&
                                (() => {
                                  const hasRefund =
                                    t.refundId &&
                                    String(t.refundId) !== "0" &&
                                    t.refundStatus;
                                  if (hasRefund) {
                                    const statusText = String(t.refundStatus);
                                    const truncated =
                                      statusText.length > 20
                                        ? statusText.slice(0, 20) + "…"
                                        : statusText;
                                    return (
                                      <button
                                        className="tx-refund-btn"
                                        disabled
                                        title={statusText}
                                        style={{
                                          cursor: "not-allowed",
                                          opacity: 0.7,
                                          maxWidth: 180,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <i
                                          className="mdi mdi-cash-refund"
                                          style={{ fontSize: 13 }}
                                        ></i>{" "}
                                        <span
                                          style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {truncated}
                                        </span>
                                      </button>
                                    );
                                  }
                                  const noRefund =
                                    (!t.refundId ||
                                      String(t.refundId) === "0") &&
                                    !t.refundStatus;
                                  if (noRefund) {
                                    return (
                                      <button
                                        className="tx-refund-btn"
                                        onClick={(e) => openRefundModal(t, e)}
                                      >
                                        <i
                                          className="mdi mdi-cash-refund"
                                          style={{ fontSize: 13 }}
                                        ></i>{" "}
                                        Refund
                                      </button>
                                    );
                                  }
                                  return null;
                                })()}
                            </td>
                            <td className="tx-cd">
                              {fmtDate(t.paymentCreatedAt || t.txnDate)}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                className="tx-arw"
                                onClick={() => openDrawer(t)}
                              >
                                <i className="mdi mdi-chevron-right"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {loading && (
                <div className="tx-loading">
                  <i
                    className="mdi mdi-loading mdi-spin"
                    style={{ fontSize: 18 }}
                  ></i>{" "}
                  Loading transactions…
                </div>
              )}

              {!loading && txnList.length === 0 && (
                <div className="tx-empty">
                  <div className="tx-empty-icon">
                    <i className="mdi mdi-file-document-outline"></i>
                  </div>
                  <div className="tx-empty-t">No transactions found</div>
                  <div className="tx-empty-s">
                    Try adjusting your filters or search
                  </div>
                </div>
              )}

              {summary.totalPages > 1 && (
                <div className="tx-pag">
                  <span className="tx-pi">
                    Page {currentPage} of {summary.totalPages} &middot;{" "}
                    {summary.totalRecords} results
                  </span>
                  <div className="tx-pbs">
                    <button
                      className="tx-pb"
                      disabled={currentPage <= 1}
                      onClick={() => {
                        const p = currentPage - 1;
                        setCurrentPage(p);
                        if (p === 1) fetchTransactions({ page: 1 });
                      }}
                    >
                      <i className="mdi mdi-chevron-left"></i> Prev
                    </button>
                    {Array.from(
                      { length: Math.min(summary.totalPages, 7) },
                      (_, i) => i + 1
                    ).map((p) => (
                      <button
                        key={p}
                        className={`tx-pb ${currentPage === p ? "active" : ""}`}
                        onClick={() => {
                          setCurrentPage(p);
                          if (p === 1) fetchTransactions({ page: 1 });
                        }}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      className="tx-pb"
                      disabled={currentPage >= summary.totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next <i className="mdi mdi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* ═══ DRAWER ═══ */}
      <div
        className={`tx-dov ${drawerOpen ? "open" : ""}`}
        onClick={closeDrawer}
      ></div>
      {drawerOpen && (
        <button className="tx-dr-mobile-close" onClick={closeDrawer}>
          <i className="mdi mdi-close"></i>
        </button>
      )}
      <div className={`tx-dr ${drawerOpen ? "open" : ""}`}>
        {drawerItem &&
          (() => {
            const t = drawerItem;
            const status = mapStatus(t.status);
            const isSucceeded = status === "succeeded";
            const isFailed = status === "failed";
            const isProcessing = status === "processing"; // ← This was missing!
            const isPending = status === "pending";
            const isCancelled = status === "cancelled";
            const displayProductName =
              t.productDetails && t.productDetails.length > 0
                ? t.productDetails[0].productName
                : t.productName || "—";

            return (
              <>
                <div className="tx-drh">
                  <span className="tx-drt">Payment Details</span>
                  <button className="tx-drc" onClick={closeDrawer}>
                    <i className="mdi mdi-close"></i>
                  </button>
                </div>

                <div className="tx-ds">
                  <div className="tx-dst">Identifiers</div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Transaction ID</span>
                    <span className="tx-dv mono">#{t.transactionId}</span>
                  </div>
                  {t.paymentLinkId && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Payment Link ID</span>
                      <span className="tx-dv mono">{t.paymentLinkId}</span>
                    </div>
                  )}
                  {t.subscriptionId && t.subscriptionId !== "0" && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Subscription ID</span>
                      <span className="tx-dv mono">{t.subscriptionId}</span>
                    </div>
                  )}
                  <div className="tx-dr-row">
                    <span className="tx-dk">Customer ID</span>
                    <span className="tx-dv mono">{t.customerId || "—"}</span>
                  </div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Customer Email</span>
                    <span className="tx-dv">{t.customerEmail || "—"}</span>
                  </div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Customer Identity</span>
                    <span className="tx-dv">{t.customerIdentity || "—"}</span>
                  </div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Payment Type</span>
                    <span className="tx-dv">{t.paymentType || "—"}</span>
                  </div>
                </div>

                <div className="tx-ds">
                  <div className="tx-dst">Transaction</div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Product</span>
                    <span className="tx-dv">{displayProductName}</span>
                  </div>
                  {t.productDetails && t.productDetails.length > 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Product Details</span>
                      <span className="tx-dv">
                        {t.productDetails[0].qty} x{" "}
                        {fmtAmt(
                          t.productDetails[0].itemPrice,
                          t.productDetails[0].currency
                        )}
                      </span>
                    </div>
                  )}
                  <div className="tx-dr-row">
                    <span className="tx-dk">Currency</span>
                    <span className="tx-dv">{t.currency}</span>
                  </div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Base Amount</span>
                    <span className="tx-dv">
                      {fmtAmt(t.baseAmount, t.currency)}
                    </span>
                  </div>
                  {t.discountAmount > 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Discount</span>
                      <span className="tx-dv red">
                        -{fmtAmt(t.discountAmount, t.currency)}
                      </span>
                    </div>
                  )}
                  <div className="tx-dr-row">
                    <span className="tx-dk">Fee Amount</span>
                    <span className="tx-dv">
                      {fmtAmt(t.feeAmount, t.currency)}
                    </span>
                  </div>
                  {t.paidAmount > 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Amount Paid</span>
                      <span className="tx-dv green">
                        {fmtAmt(t.paidAmount, t.currency)}
                      </span>
                    </div>
                  )}
                  {t.storeCrEarned > 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Store Credit Earned</span>
                      <span className="tx-dv" style={{ color: "#10b981" }}>
                        {fmtAmt(t.storeCrEarned, t.currency)}
                      </span>
                    </div>
                  )}
                  {t.storeCrRedeemed > 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Store Credit Redeemed</span>
                      <span className="tx-dv" style={{ color: "#818cf8" }}>
                        {fmtAmt(t.storeCrRedeemed, t.currency)}
                      </span>
                    </div>
                  )}
                  {t.cashbackEarned > 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Cashback Earned</span>
                      <span className="tx-dv" style={{ color: "#f59e0b" }}>
                        {fmtAmt(t.cashbackEarned, t.currency)}
                      </span>
                    </div>
                  )}
                  {t.cashbackRedeemed != 0 && (
                    <div className="tx-dr-row">
                      <span className="tx-dk">Cashback Redeemed</span>
                      <span className="tx-dv" style={{ color: "#f87171" }}>
                        {fmtAmt(t.cashbackRedeemed, t.currency)}
                      </span>
                    </div>
                  )}
                  <div className="tx-dr-row">
                    <span className="tx-dk">Test Mode</span>
                    <span className="tx-dv">
                      {t.testMode === true ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="tx-dr-row">
                    <span className="tx-dk">Action Required</span>
                    <span className="tx-dv">
                      {t.actionRequired ? "Yes" : "No"}
                    </span>
                  </div>
                </div>

                {(t.paymentSubDetails || t.sessionId || t.transactionAddress) && (
                  <div className="tx-ds">
                    <div className="tx-dst">Payment Details</div>
                    {t.paymentMethod && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Payment Method</span>
                        <span className="tx-dv">{t.paymentMethod}</span>
                      </div>
                    )}
                    {t.paymentSubDetails && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Network</span>
                        <span className="tx-dv">{t.paymentSubDetails}</span>
                      </div>
                    )}
                    {t.sessionId && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Session ID</span>
                        <span className="tx-dv mono">{t.sessionId}</span>
                      </div>
                    )}
                    <div className="tx-dr-row">
                      <span className="tx-dk">Recipient Address</span>
                      <span className="tx-dv mono">
                        {t.transactionAddress && t.transactionAddress.trim() !== "" ? t.transactionAddress : "—"}
                      </span>
                    </div>
                    {t.intentId && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Intent ID</span>
                        <span className="tx-dv mono">{t.intentId}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="tx-ds">
                  <div className="tx-dst">Status Timeline</div>
                  <div className="tx-tl">
                    <div className="tx-tli">
                      <div className="tx-tld done"></div>
                      <div>
                        <div className="tx-tll">Created</div>
                        <div className="tx-tlt">
                          {fmtDate(t.paymentCreatedAt)}
                        </div>
                      </div>
                    </div>

                    {/* Processing step - shows as "current" for payment_created */}
                    <div className="tx-tli">
                      <div
                        className={`tx-tld ${
                          isSucceeded || isFailed || isCancelled
                            ? "done"
                            : isProcessing
                            ? "current"
                            : "pnd"
                        }`}
                      ></div>
                      <div>
                        <div className="tx-tll">Processing</div>
                        <div className="tx-tlt">
                          {isProcessing
                            ? "Awaiting payment confirmation"
                            : t.paymentInitiatedAt
                            ? fmtDate(t.paymentInitiatedAt)
                            : "—"}
                        </div>
                      </div>
                    </div>

                    {/* Failed state */}
                    {isFailed && (
                      <div className="tx-tli">
                        <div className="tx-tld fail"></div>
                        <div>
                          <div className="tx-tll" style={{ color: "#ef4444" }}>
                            Failed / Expired
                          </div>
                          <div className="tx-tlt">{t.status}</div>
                        </div>
                      </div>
                    )}

                    {/* Succeeded state */}
                    {isSucceeded && (
                      <div className="tx-tli">
                        <div className="tx-tld done"></div>
                        <div>
                          <div className="tx-tll" style={{ color: "#10b981" }}>
                            Succeeded
                          </div>
                          <div className="tx-tlt">
                            {t.paymentProcessAt
                              ? fmtDate(t.paymentProcessAt)
                              : fmtDate(t.paymentCreatedAt)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Refunded state */}
                    {t.status === "refunded" && (
                      <div className="tx-tli">
                        <div
                          className="tx-tld"
                          style={{
                            background: "#6366f1",
                            borderColor: "#6366f1",
                          }}
                        ></div>
                        <div>
                          <div className="tx-tll" style={{ color: "#818cf8" }}>
                            Refunded
                          </div>
                          <div className="tx-tlt">
                            {fmtDate(t.updated || t.paymentCreatedAt)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Cancelled state */}
                    {isCancelled && (
                      <div className="tx-tli">
                        <div
                          className="tx-tld"
                          style={{
                            background: "#ef4444",
                            borderColor: "#ef4444",
                          }}
                        ></div>
                        <div>
                          <div className="tx-tll" style={{ color: "#ef4444" }}>
                            Subscription Cancelled
                          </div>
                          <div className="tx-tlt">
                            {fmtDate(t.paymentCreatedAt)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actual Pending state (rare - only for status = "pending") */}
                    {isPending && (
                      <div className="tx-tli">
                        <div className="tx-tld current"></div>
                        <div>
                          <div className="tx-tll" style={{ color: "#06b6d4" }}>
                            Awaiting Action
                          </div>
                          <div className="tx-tlt">
                            {t.subscriptionStatus
                              ? `Subscription ${cap(t.subscriptionStatus)}`
                              : "Waiting for customer"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {(t.paymentType === "RECURRING" ||
                  t.paymentType === "SUBSCRIPTION" ||
                  (t.cycle && t.cycle !== "0")) && (
                  <div className="tx-ds">
                    <div className="tx-dst">Subscription Info</div>
                    {t.subscriptionStatus && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Subscription Status</span>
                        <span className="tx-dv">
                          <span
                            className={`tx-tag ${
                              t.subscriptionStatus === "active"
                                ? "sub"
                                : t.subscriptionStatus === "trialing"
                                ? "onetime"
                                : t.subscriptionStatus === "cancelled"
                                ? ""
                                : "test"
                            }`}
                            style={
                              t.subscriptionStatus === "cancelled"
                                ? {
                                    background: "rgba(239,68,68,.1)",
                                    color: "#ef4444",
                                  }
                                : {}
                            }
                          >
                            {cap(t.subscriptionStatus)}
                          </span>
                        </span>
                      </div>
                    )}
                    {t.trialDays > 0 && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Trial Days</span>
                        <span className="tx-dv">{t.trialDays} days</span>
                      </div>
                    )}
                    {t.cycle && t.cycle !== "0" && (
                      <div className="tx-dr-row">
                        <span className="tx-dk">Billing Cycle</span>
                        <span className="tx-dv">{t.cycle}</span>
                      </div>
                    )}
                    {t.nextBillingDate &&
                      t.subscriptionStatus !== "cancelled" && (
                        <div className="tx-dr-row">
                          <span className="tx-dk">Next Billing</span>
                          <span className="tx-dv">
                            {fmtDate(t.nextBillingDate)}
                          </span>
                        </div>
                      )}
                    {(t.subscriptionStatus === "active" ||
                      t.subscriptionStatus === "trialing") && (
                      <div style={{ marginTop: 12 }}>
                        <button
                          className="tx-cancel-btn"
                          onClick={() => handleCancelSubscription(t)}
                          disabled={cancelingId === t.transactionId}
                        >
                          <i
                            className="mdi mdi-cancel"
                            style={{ fontSize: 13 }}
                          ></i>
                          {cancelingId === t.transactionId
                            ? "Canceling..."
                            : "Cancel Subscription"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
      </div>
      {/* ═══ REFUND MODAL ═══ */}
      <div
        className={`tx-modal-ov ${refundOpen ? "open" : ""}`}
        onClick={closeRefundModal}
      >
        <div className="tx-modal" onClick={(e) => e.stopPropagation()}>
          {refundItem &&
            (() => {
              const t = refundItem;
              const totalPaid = (t.baseAmount || 0) + (t.feeAmount || 0);
              const currency = t.currency || "BTC";

              return (
                <>
                  <div className="tx-modal-hdr">
                    <span className="tx-modal-title">
                      <i className="mdi mdi-cash-refund"></i> Issue Refund
                    </span>
                    <button
                      className="tx-modal-close"
                      onClick={closeRefundModal}
                    >
                      <i className="mdi mdi-close"></i>
                    </button>
                  </div>

                  <div className="tx-modal-body">
                    {/* Invoice & Total Paid */}
                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Invoice</span>
                      <span className="tx-modal-val mono">
                        #{t.transactionId}
                      </span>
                    </div>
                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Total Payable</span>
                      <span className="tx-modal-val highlight">
                        {fmtAmt(totalPaid, currency)}
                      </span>
                    </div>

                    <div className="tx-modal-divider"></div>

                    {/* Refund Amount */}
                    <div className="tx-modal-field">
                      <label>Refund Amount ({currency})</label>
                      <input
                        className="tx-modal-input"
                        type="number"
                        step="any"
                        min="0"
                        max={totalPaid}
                        placeholder={`0.00 ${currency}`}
                        value={refundAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRefundAmount(val);
                          setRefundError("");
                          clearTimeout(networkFeeTimerRef.current);
                          networkFeeTimerRef.current = setTimeout(() => {
                            fetchNetworkFees(refundItem, val);
                          }, 400);
                        }}
                      />
                    </div>

                    {/* Fee & Summary */}
                    <div className="tx-modal-summary">
                      <div className="tx-modal-summary-row">
                        <span style={{ opacity: 0.5 }}>Refund Amount</span>
                        <span>
                          {fmtAmt(parseFloat(refundAmount) || 0, currency)}
                        </span>
                      </div>
                      {!isBrandWallet && (
                        <div className="tx-modal-summary-row">
                          <span style={{ opacity: 0.5 }}>
                            Network Fee{" "}
                            <span style={{ fontSize: 11, opacity: 0.6 }}>
                              (PayBito Admin)
                            </span>
                          </span>
                          <span style={{ fontWeight: "500" }}>
                            {networkFeeLoading
                              ? "…"
                              : fmtAmt(networkFee, currency)}
                          </span>
                        </div>
                      )}

                      <div className="tx-modal-summary-row total">
                        <span style={{ fontWeight: "600" }}>
                          Total Deducted from Wallet
                        </span>
                        <span style={{ color: "#818cf8", fontWeight: "600" }}>
                          {fmtAmt(refundTotal, currency)}
                        </span>
                      </div>
                    </div>

                    {/* Wallet Balance */}
                    <div className="tx-modal-wallet">
                      <i className="mdi mdi-wallet"></i>
                      <div className="tx-modal-wallet-info">
                        <div className="tx-modal-wallet-lbl">
                          Available in Merchant Wallet
                        </div>
                        <div className="tx-modal-wallet-val">
                          {walletBalanceLoading ? (
                            <span style={{ opacity: 0.6 }}>Loading...</span>
                          ) : (
                            fmtAmt(walletBalance, currency)
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Fee Breakdown Info */}
                    <div
                      className="tx-modal-info"
                      style={{
                        marginTop: "16px",
                        padding: "12px",
                        backgroundColor: "rgba(129, 140, 248, 0.08)",
                        borderLeft: "3px solid #818cf8",
                        borderRadius: "4px",
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: 12,
                      }}
                    >
                      <p style={{ margin: "0 0 6px 0" }}>
                        <strong>Network Fee:</strong> PayBito processing fee
                      </p>
                      {/* <p style={{ margin: "0 0 6px 0" }}>
                        <strong>Platform Fee:</strong> PayBito processing fee
                      </p> */}
                      <p style={{ margin: "0" }}>
                        <strong>Total:</strong> Sum of all fees deducted from
                        your wallet
                      </p>
                    </div>

                    {/* Error */}
                    {refundError && (
                      <div className="tx-modal-error">
                        <i className="mdi mdi-alert-circle"></i>
                        <span>{refundError}</span>
                      </div>
                    )}

                    {/* Reason */}
                    <div className="tx-modal-field">
                      <label>Refund Reason</label>
                      <select
                        className="tx-modal-select"
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                      >
                        <option value="Customer Request">
                          Customer Request
                        </option>
                        <option value="Order Cancelled">Order Cancelled</option>
                        <option value="Order Not Fulfilled">
                          Order Not Fulfilled
                        </option>
                        <option value="Duplicate Payment">
                          Duplicate Payment
                        </option>
                        <option value="Incorrect Amount Charged">
                          Incorrect Amount Charged
                        </option>
                        <option value="Overpayment Correction">
                          Overpayment Correction
                        </option>
                        <option value="Suspected Fraud">Suspected Fraud</option>
                        <option value="Merchant Error">Merchant Error</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Note */}
                    <div className="tx-modal-field" style={{ marginBottom: 0 }}>
                      <label>
                        Note{" "}
                        <span
                          style={{
                            opacity: 0.5,
                            fontWeight: 400,
                            textTransform: "none",
                          }}
                        >
                          (optional)
                        </span>
                      </label>
                      <textarea
                        className="tx-modal-textarea"
                        placeholder="Add any additional notes..."
                        value={refundNote}
                        onChange={(e) => setRefundNote(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="tx-modal-footer">
                    <button
                      className="tx-modal-btn-cancel"
                      onClick={closeRefundModal}
                    >
                      Cancel
                    </button>
                    <button
                      className="tx-modal-btn-refund"
                      onClick={handleIssueRefund}
                      disabled={refundProcessing || !refundAmount}
                    >
                      {refundProcessing ? (
                        <>
                          <i
                            className="mdi mdi-loading mdi-spin"
                            style={{ fontSize: 15 }}
                          ></i>{" "}
                          Processing...
                        </>
                      ) : (
                        <>
                          <i
                            className="mdi mdi-check-circle"
                            style={{ fontSize: 15 }}
                          ></i>{" "}
                          Issue Refund
                        </>
                      )}
                    </button>
                  </div>
                </>
              );
            })()}
        </div>
      </div>

      {/* ═══ REFUND HISTORY TABLE ═══ */}
      {refundHistory && refundHistory.length > 0 && (
        <div
          style={{
            marginTop: 48,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            paddingTop: 40,
          }}
        >
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <i className="mdi mdi-history"></i> Refund History
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table className="tx-table" style={{ minWidth: "100%" }}>
              <thead>
                <tr>
                  <th>Refund Date</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Invoice ID</th>
                  <th>Refund ID</th>
                  <th>Refund Amount</th>
                  <th>Reason</th>
                  <th>Note</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {refundHistory.map((ref, idx) => {
                  const statusLabel = () => {
                    const s = ref.status?.toUpperCase() || "PENDING_ADDRESS";
                    if (s === "PENDING_ADDRESS") return "Waiting for Address";
                    if (s === "ADDRESS_RECEIVED") return "Address Received";
                    if (s === "SENT_TO_BATCH") return "Sent to Processing";
                    if (s === "BATCH_APPROVED") return "Completed";
                    if (s === "REFUND_ADDRESS_AVAILABLE")
                      return "Address Available";
                    return cap(s.replace(/_/g, " "));
                  };

                  return (
                    <tr key={idx}>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {ref.createdDate ? fmtDate(ref.createdDate) : "—"}
                      </td>
                      <td>{ref.customerName || ref.customer || "—"}</td>
                      <td style={{ fontSize: 12 }}>
                        {ref.customerEmail || ref.email || "—"}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        #{ref.invoiceId || ref.transactionId || "—"}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {ref.refundId || ref.id || "—"}
                      </td>
                      <td>
                        <span style={{ fontWeight: 500 }}>
                          {fmtAmt(ref.amount, ref.currency || "BTC")}
                        </span>
                      </td>
                      <td
                        style={{
                          fontSize: 12,
                          maxWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ref.reason || "—"}
                      </td>
                      <td
                        style={{
                          fontSize: 12,
                          maxWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ref.note || "—"}
                      </td>
                      <td>
                        <span
                          className={`tx-tag ${
                            ref.status?.includes("BATCH_APPROVED") ||
                            ref.status?.includes("completed")
                              ? "success"
                              : ref.status?.includes("ADDRESS_RECEIVED") ||
                                ref.status?.includes("SENT_TO_BATCH")
                              ? "processing"
                              : "pending"
                          }`}
                          style={{ fontSize: 11, padding: "4px 8px" }}
                        >
                          {statusLabel()}
                        </span>
                      </td>
                      <td>
                        <button
                          className="tx-action-btn"
                          onClick={() => openRefundDetails(ref)}
                          style={{
                            background: "rgba(129,140,248,0.1)",
                            border: "1px solid rgba(129,140,248,0.3)",
                            color: "#818cf8",
                            padding: "4px 12px",
                            borderRadius: 4,
                            fontSize: 11,
                            cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "rgba(129,140,248,0.2)";
                            e.target.style.borderColor =
                              "rgba(129,140,248,0.5)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "rgba(129,140,248,0.1)";
                            e.target.style.borderColor =
                              "rgba(129,140,248,0.3)";
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ REFUND DETAILS MODAL ═══ */}
      <div
        className={`tx-modal-ov ${refundDetailsOpen ? "open" : ""}`}
        onClick={closeRefundDetails}
      >
        <div className="tx-modal" onClick={(e) => e.stopPropagation()}>
          {selectedRefund &&
            (() => {
              const ref = selectedRefund;
              const statusLabel = () => {
                const s = ref.status?.toUpperCase() || "PENDING_ADDRESS";
                if (s === "PENDING_ADDRESS")
                  return "Waiting for customer submission";
                if (s === "ADDRESS_RECEIVED") return "Address Received";
                if (s === "SENT_TO_BATCH")
                  return "Submitted to PayBito Batch Operations for processing";
                if (s === "BATCH_APPROVED") return "Batch Approved";
                if (s === "REFUND_ADDRESS_AVAILABLE")
                  return "Refund Address Available";
                return cap(s.replace(/_/g, " "));
              };

              return (
                <>
                  <div className="tx-modal-hdr">
                    <span className="tx-modal-title">
                      <i className="mdi mdi-information"></i> Refund Details
                    </span>
                    <button
                      className="tx-modal-close"
                      onClick={closeRefundDetails}
                    >
                      <i className="mdi mdi-close"></i>
                    </button>
                  </div>

                  <div className="tx-modal-body">
                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Refund ID</span>
                      <span className="tx-modal-val mono">
                        {ref.refundId || ref.id || "—"}
                      </span>
                    </div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Invoice ID</span>
                      <span className="tx-modal-val mono">
                        #{ref.invoiceId || ref.transactionId || "—"}
                      </span>
                    </div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Customer</span>
                      <span className="tx-modal-val">
                        {ref.customerName || ref.customer || "—"}
                      </span>
                    </div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Email</span>
                      <span className="tx-modal-val">
                        {ref.customerEmail || ref.email || "—"}
                      </span>
                    </div>

                    <div className="tx-modal-divider"></div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Amount</span>
                      <span className="tx-modal-val highlight">
                        {fmtAmt(ref.amount, ref.currency || "BTC")}
                      </span>
                    </div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Network Fee</span>
                      <span className="tx-modal-val">
                        {fmtAmt(
                          ref.networkFee || 0.0005,
                          ref.currency || "BTC"
                        )}
                      </span>
                    </div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Total Deducted</span>
                      <span className="tx-modal-val highlight">
                        {fmtAmt(
                          (ref.amount || 0) + (ref.networkFee || 0.0005),
                          ref.currency || "BTC"
                        )}
                      </span>
                    </div>

                    <div className="tx-modal-divider"></div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Reason</span>
                      <span className="tx-modal-val">{ref.reason || "—"}</span>
                    </div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Note</span>
                      <span className="tx-modal-val">{ref.note || "—"}</span>
                    </div>

                    <div className="tx-modal-divider"></div>

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Status</span>
                      <span
                        className={`tx-tag ${
                          ref.status?.includes("BATCH_APPROVED") ||
                          ref.status?.includes("completed")
                            ? "success"
                            : ref.status?.includes("ADDRESS_RECEIVED") ||
                              ref.status?.includes("SENT_TO_BATCH")
                            ? "processing"
                            : "pending"
                        }`}
                        style={{ fontSize: 12, padding: "6px 12px" }}
                      >
                        {statusLabel()}
                      </span>
                    </div>

                    {ref.refundAddress && (
                      <div className="tx-modal-row">
                        <span className="tx-modal-lbl">Refund Address</span>
                        <span
                          className="tx-modal-val mono"
                          style={{ wordBreak: "break-all", fontSize: 11 }}
                        >
                          {ref.refundAddress}
                        </span>
                      </div>
                    )}

                    {ref.blockchainTransaction && (
                      <div className="tx-modal-row">
                        <span className="tx-modal-lbl">Blockchain Tx</span>
                        <span
                          className="tx-modal-val mono"
                          style={{ wordBreak: "break-all", fontSize: 11 }}
                        >
                          {ref.blockchainTransaction}
                        </span>
                      </div>
                    )}

                    <div className="tx-modal-row">
                      <span className="tx-modal-lbl">Created</span>
                      <span
                        className="tx-modal-val mono"
                        style={{ fontSize: 11 }}
                      >
                        {ref.createdDate ? fmtDate(ref.createdDate) : "—"}
                      </span>
                    </div>

                    {ref.updatedDate && (
                      <div className="tx-modal-row">
                        <span className="tx-modal-lbl">Last Updated</span>
                        <span
                          className="tx-modal-val mono"
                          style={{ fontSize: 11 }}
                        >
                          {fmtDate(ref.updatedDate)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="tx-modal-footer">
                    <button
                      className="tx-modal-btn-cancel"
                      onClick={closeRefundDetails}
                    >
                      Close
                    </button>
                  </div>
                </>
              );
            })()}
        </div>
      </div>
    </Fragment>
  );
};

export default Transactions;
